import hiringManager from "@/assets/boss.png";
import logo from '@/assets/hiredcoach-logo.svg';
import Seo from "@/components/marketing/Seo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { useApiClient } from "@/lib/api";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

const Agreements = () => {
  const [privacy, setPrivacy] = useState<string>("");
  const [terms, setTerms] = useState<string>("");
  const { user, refreshAuthSnapshot, logout } = useAuth();
  const { api } = useApiClient();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const acceptMode = params.get('accept') === '1';
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);

  // Lazy import markdown assets generated by Vite for src URLs
  // These imports resolve to URLs we can fetch as text
  const assets = useMemo(() => ({
    privacy: new URL("../assets/agreements/privacy_2025_09_06.md", import.meta.url).toString(),
    terms: new URL("../assets/agreements/terms_of_use_2025_09_06.md", import.meta.url).toString(),
  }), []);

  useEffect(() => {
    const load = async () => {
      try {
        const p = await fetch(assets.privacy);
        if (p.ok) setPrivacy(await p.text());
      } catch { }
      try {
        const t = await fetch(assets.terms);
        if (t.ok) setTerms(await t.text());
      } catch { }
    };
    load();
  }, [assets]);

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="User Agreement and Privacy Policy | HiredCoach"
        description="Review the HiredCoach user agreement and privacy policy."
        canonicalPath="/agreements"
      />
      {/* Header - styled to match landing */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <nav className="flex justify-between items-center">
            <Link to="/" className="flex items-center min-w-0 flex-shrink hover:opacity-80 transition-opacity">
              <img src={logo} alt="HiredCoach Logo" className="w-8 h-8 sm:w-10 sm:h-10 mr-1 sm:mr-2 flex-shrink-0" />
              <span className="font-bold text-lg sm:text-xl truncate">
                <span style={{ color: '#4f9ecb' }}>Hired</span><span style={{ color: '#334763' }}>Coach</span>
              </span>
            </Link>
            <div className="flex gap-1 sm:gap-2 md:gap-4 flex-shrink-0">
              {!user ? (
                <>
                  <Button variant="ghost" className="text-gray-600 bg-gray-100 hover:bg-gray-200 hover:text-gray-900 text-sm sm:text-base px-2 sm:px-4" asChild>
                    <Link to="/login">Sign In</Link>
                  </Button>
                  <Button style={{ backgroundColor: '#4f9ecb' }} className="text-white hover:opacity-90 text-sm sm:text-base px-2 sm:px-4" asChild>
                    <Link to="/register">Sign Up</Link>
                  </Button>
                </>
              ) : (
                <Button variant="ghost" className="text-gray-600 bg-gray-100 hover:bg-gray-200 hover:text-gray-900 text-sm sm:text-base px-2 sm:px-4" onClick={logout}>
                  Log out
                </Button>
              )}
            </div>
          </nav>
        </div>
      </header>

      {/* Hero-like header with boss image */}
      <section className="bg-gray-50">
        <div className="container mx-auto px-6 py-10">
          <div className="flex justify-center mt-6">
            <img src={hiringManager} alt="Coach" className="h-20 md:h-36 w-auto object-contain" />
          </div>
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Agreements</h1>
            {user && (
              <p className="text-gray-700 text-sm md:text-base">
                Before you continue, we need you to agree to our latest policies.
              </p>
            )}
          </div>
        </div>
      </section>

      <div className="container mx-auto px-6 py-8 max-w-5xl">
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="p-4 border border-gray-200">
            <h2 className="text-xl font-semibold mb-2">User Agreement</h2>
            <div className="h-96 overflow-y-auto text-sm text-gray-800 rounded border border-gray-100 p-3 bg-gray-50">
              <pre className="whitespace-pre-wrap">{terms || "Loading..."}</pre>
            </div>
          </Card>
          <Card className="p-4 border border-gray-200">
            <h2 className="text-xl font-semibold mb-2">Privacy Policy</h2>
            <div className="h-96 overflow-y-auto text-sm text-gray-800 rounded border border-gray-100 p-3 bg-gray-50">
              <pre className="whitespace-pre-wrap">{privacy || "Loading..."}</pre>
            </div>
          </Card>
        </div>

        {acceptMode ? (
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <div className="flex items-center gap-2 text-sm">
              <Checkbox checked={agreeTerms} onCheckedChange={(v: any) => setAgreeTerms(!!v)} />
              <span>I agree to the User Agreement</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Checkbox checked={agreePrivacy} onCheckedChange={(v: any) => setAgreePrivacy(!!v)} />
              <span>I agree to the Privacy Policy</span>
            </div>
            <div className="md:col-span-2 flex justify-center">
              <Button disabled={!agreePrivacy || !agreeTerms} onClick={async () => {
                await api.acceptAgreementsV1(agreePrivacy, agreeTerms);
                await refreshAuthSnapshot();
                // If logged in, send them to the dashboard after accepting
                if (user) {
                  navigate('/dashboard');
                }
              }}>Accept</Button>
            </div>
          </div>
        ) : (
          <div className="mt-8 flex justify-center">
            <Button asChild>
              <Link to="/register">Create an account</Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Agreements;

